"use client";

import { useCallback, useRef, useEffect } from "react";
import { track } from "../../core/client";
import { clearSearchIntentId } from "../../core/ids";

/**
 * 검색 수행 추적 훅 (내부용)
 */
export function useSearchTracking() {
    const renderSeqRef = useRef(0);

    const trackSearch = useCallback(
        (
            query: string,
            resultCount: number,
            searchType: "stock" | "unified" | "watchlist" | 'portfolio',
            latencyMs?: number
        ) => {
            renderSeqRef.current += 1;

            track("search_performed", {
                query, // 🔍 검색어 추가!
                search_type: searchType,
                result_count: resultCount,
                render_seq: renderSeqRef.current,
                latency_ms: latencyMs || undefined,
            });
        },
        []
    );

    const resetRenderSeq = useCallback(() => {
        renderSeqRef.current = 0;
    }, []);

    return { trackSearch, resetRenderSeq };
}

/**
 * 검색 결과 자동 추적 래퍼 (🎯 사용자용)
 * 검색 API 호출과 tracking을 자동으로 처리
 */
export function useTrackedSearchResults(
    searchQuery: string,
    searchType: "stock" | "unified" | "watchlist" | 'portfolio' = "unified"
) {
    const { trackSearch, resetRenderSeq } = useSearchTracking();
    const searchStartTimeRef = useRef<number | null>(null);
    const lastTrackedQueryRef = useRef<string | null>(null);
    const trackTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // 검색 시작 시간 기록
    useEffect(() => {
        if (searchQuery && searchQuery.length > 0) {
            searchStartTimeRef.current = Date.now();
        }
    }, [searchQuery]);

    // 검색 모달 닫힐 때 초기화
    useEffect(() => {
        return () => {
            // pending된 track 취소
            if (trackTimeoutRef.current) {
                clearTimeout(trackTimeoutRef.current);
                trackTimeoutRef.current = null;
            }
            clearSearchIntentId();
            resetRenderSeq();
        };
    }, [resetRenderSeq]);

    // 검색 완료 추적 (debounce 적용으로 빠른 입력/삭제 무시)
    const trackIfReady = useCallback(
        (data: any[] | null, isValid: boolean) => {
            // 기존 pending된 track 취소
            if (trackTimeoutRef.current) {
                clearTimeout(trackTimeoutRef.current);
                trackTimeoutRef.current = null;
            }

            // 로딩 완료 && 검색어 있음 && 아직 추적 안함
            if (
                !isValid &&
                data &&
                searchQuery &&
                searchQuery.length > 0 &&
                lastTrackedQueryRef.current !== searchQuery
            ) {
                // 200ms debounce: 빠른 연속 입력/삭제 방지
                trackTimeoutRef.current = setTimeout(() => {
                    // debounce 후에도 searchQuery가 여전히 유효한지 재확인
                    if (
                        searchQuery &&
                        searchQuery.length > 0 &&
                        lastTrackedQueryRef.current !== searchQuery
                    ) {
                        const latencyMs = searchStartTimeRef.current
                            ? Date.now() - searchStartTimeRef.current
                            : undefined;

                        trackSearch(searchQuery, data.length || 0, searchType, latencyMs);

                        lastTrackedQueryRef.current = searchQuery;
                        searchStartTimeRef.current = null;
                    }
                    trackTimeoutRef.current = null;
                }, 200);
            }
        },
        [searchQuery, searchType, trackSearch]
    );

    return { trackIfReady };
}

